import { useEffect, useMemo, useRef, useState } from 'react';
import { ImagenCuestionario } from './ImagenCuestionario';
import { FilaPlegada } from './FilaPlegada';
import {
  abreEnPantallaCompleta,
  estaRespondida,
  type RespuestaLocal,
  type Respuestas,
} from './respuestaLocal';
import type { Escala, Pregunta } from '@copathy/shared';
import { esSeccion } from '@copathy/shared';
import { PreguntaRespondible, acentoDeColor, type Acento } from './PreguntaRespondible';

/**
 * El cuestionario completo **dentro de un teléfono**, paginado por secciones.
 *
 * ── Por qué un marco de teléfono y no una columna angosta ──
 * El trabajador responde desde la app móvil (D-011). Una columna angosta en una
 * página de escritorio se lee como «contenido apretado»; un teléfono dibujado se
 * lee como lo que es: **así se va a ver donde de verdad se responde**. Es el
 * adelanto de la app móvil que todavía no existe — cuando exista, este marco es
 * lo que la valida contra el instrumento real.
 *
 * ── Las páginas ──
 * Cada **sección** abre una página, con su título, subtítulo y color de acento.
 * Página 0 es la portada: nombre, descripción y «Comenzar». Las preguntas que
 * van antes de la primera sección forman una página sin cabecera.
 *
 * La paginación no es decoración: treinta preguntas en una columna son un
 * formulario que no se termina nunca. Partido en páginas con barra de progreso,
 * cada «Siguiente» es una meta corta — es lo que en AppSheet no se podía hacer y
 * la gente abandonaba.
 *
 * ── Qué es interactivo, y por qué dejó de ser una maqueta ──
 * **Todo**: la navegación y las preguntas. Decía «las preguntas son inertes: acá
 * no se responde nada», y era verdad hasta CC-12 — pero el recorrido que se
 * eligió es **una lista con las preguntas plegadas mostrando lo que ya se
 * respondió**, y eso no se puede ni dibujar sin respuestas. Sin ellas la previa
 * enseñaría filas vacías, y lo que hay que poder juzgar acá es justamente si «De
 * acuerdo» cabe en una línea o si una matriz de seis se resume en algo legible.
 *
 * ⚠️ **Las respuestas viven y mueren en el navegador.** No se guardan, no se
 * envían y no tocan la base: esto sigue siendo la previa del constructor. El
 * envío de verdad lo hace la app con `esquemaEnviarRespuestas`.
 *
 * ⚠️ Y un aviso para quien toque `PreguntaRespondible`: **tenía `inert`** y eso
 * anulaba el puntero y el foco de todos sus descendientes. Cuando la escala pasó
 * a ser tocable (D-131) el atributo se quedó, así que los botones respondían a un
 * `click()` de script pero **una persona no podía tocarlos** — y la comprobación
 * de entonces, hecha con clics programáticos, no lo vio. No puede volver.
 */

type Pagina = {
  /** La sección que la abre, o null para la portada / preguntas sin sección. */
  seccion: Pregunta | null;
  preguntas: { pregunta: Pregunta; numero: number }[];
};

export function VistaFormulario({
  cuestionarioId,
  tienePortada = false,
  nombre,
  descripcion,
  preguntas,
  escalas,
  idResaltado,
}: {
  /**
   * De qué cuestionario es esta previa. Hace falta para pedir la portada, que se
   * sirve por una ruta autenticada y no como archivo estático.
   */
  cuestionarioId: number;
  tienePortada?: boolean;
  nombre: string;
  descripcion: string | null;
  preguntas: Pregunta[];
  escalas: Escala[];
  /** La pregunta que se está editando: se resalta y se navega hasta su página. */
  idResaltado: number | null;
}) {
  /*
    Repartir en páginas y numerar. La numeración es CONTINUA a través de las
    páginas y salta las secciones: «pregunta 7 de 23» tiene que significar lo
    mismo en la página 1 que en la 4.
  */
  const { paginas, total } = useMemo(() => {
    const lista: Pagina[] = [];
    let actual: Pagina = { seccion: null, preguntas: [] };
    let n = 0;
    for (const p of preguntas) {
      if (esSeccion(p.tipo)) {
        if (actual.seccion !== null || actual.preguntas.length > 0) lista.push(actual);
        actual = { seccion: p, preguntas: [] };
        continue;
      }
      n += 1;
      actual.preguntas.push({ pregunta: p, numero: n });
    }
    if (actual.seccion !== null || actual.preguntas.length > 0) lista.push(actual);
    return { paginas: lista, total: n };
  }, [preguntas]);

  // Página -1 es la portada. Las demás indexan `paginas`.
  const [pagina, setPagina] = useState(-1);

  /*
    Editar una pregunta lleva la previa a SU página. Sin esto, quien edita la
    pregunta 18 mira una portada — y la gracia de la previa en vivo es mirar lo
    que se está tocando.
  */
  useEffect(() => {
    if (idResaltado === null) return;
    const donde = paginas.findIndex((pg) =>
      pg.preguntas.some((x) => x.pregunta.id === idResaltado) ||
      pg.seccion?.id === idResaltado,
    );
    if (donde >= 0) setPagina(donde);
  }, [idResaltado, paginas]);

  // Si el reordenado dejó menos páginas que la que se estaba mirando, retrocede.
  useEffect(() => {
    if (pagina >= paginas.length) setPagina(paginas.length - 1);
  }, [pagina, paginas.length]);

  /*
    ── La entrada de sección a pantalla completa (CC-07) ──
    Al llegar a una página con sección, la sección ocupa la pantalla entera y
    después se contrae al banner de arriba, que es el que queda indicando a qué
    parte pertenecen las preguntas.

    Tres decisiones que no son de adorno:

    1. **Se dispara SOLO al cambiar de página**, nunca al volver a dibujar. En el
       constructor esta previa se repinta con cada tecla que se escribe: si la
       entrada dependiera del render, tocar el título de una sección haría
       parpadear la pantalla entera con cada letra.
    2. **Se salta con un toque.** Con cuatro secciones son cuatro pantallas más
       antes de responder nada; vale la pena cuando la sección orienta y estorba
       cuando solo decora, así que quien ya la leyó sigue de largo.
    3. **`prefers-reduced-motion` la apaga.** Una pantalla que se expande y se
       contrae sola es exactamente lo que ese ajuste existe para evitar. Sin
       animación el banner ya está ahí: no se pierde información, solo el efecto.
  */
  const [entrada, setEntrada] = useState<{ pagina: number; saliendo: boolean } | null>(null);
  const paginaAnterior = useRef<number | null>(null);

  useEffect(() => {
    // Solo al CAMBIAR de página, y solo hacia una que tenga sección.
    if (paginaAnterior.current === pagina) return;
    paginaAnterior.current = pagina;

    const destino = pagina >= 0 ? paginas[pagina] : undefined;
    if (!destino?.seccion) {
      setEntrada(null);
      return;
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setEntrada(null);
      return;
    }

    setEntrada({ pagina, saliendo: false });
    const aSalir = setTimeout(() => setEntrada((e) => (e ? { ...e, saliendo: true } : null)), 1500);
    return () => clearTimeout(aSalir);
  }, [pagina, paginas]);

  /*
    El desmontaje cuelga de `saliendo`, no de un reloj que arranca con la página.
    Atado al reloj de arriba, saltarla con un toque la dejaba invisible pero
    MONTADA hasta cumplirse los dos segundos: para la vista daba igual, pero un
    lector de pantalla seguía anunciando una pantalla que ya no está.
  */
  useEffect(() => {
    if (!entrada?.saliendo) return;
    const t = setTimeout(() => setEntrada(null), 500);
    return () => clearTimeout(t);
  }, [entrada?.saliendo]);

  const saltarEntrada = () => setEntrada((e) => (e ? { ...e, saliendo: true } : null));

  /*
    ── El recorrido plegado (CC-12, D-140) ──
    La página muestra las preguntas **cerradas, con la respuesta que ya se dio**,
    y se abre una a la vez. Es lo que se eligió sobre «una pregunta por pantalla»:
    en un cuestionario de veinte, avanzar de a una son veinte toques de
    «Siguiente» y revisar lo respondido obliga a recorrerlas todas hacia atrás.

    ⚠️ **Las respuestas viven acá y no en cada pregunta.** La fila plegada y el
    control desplegado tienen que mostrar lo mismo; con estado dentro de cada
    componente, la fila decía «sin responder» mientras la escala de al lado
    estaba marcada — que es exactamente lo que pasaba antes de este cambio.

    ⚠️ Y **no se guarda ni se envía nada**: esto es la previa del constructor. El
    envío de verdad lo hace la app.
  */
  const [respuestas, setRespuestas] = useState<Respuestas>({});
  /** La pregunta abierta en la lista, o null si están todas cerradas. */
  const [abierta, setAbierta] = useState<number | null>(null);
  /** La que se está respondiendo a pantalla completa. */
  const [aPantalla, setAPantalla] = useState<number | null>(null);

  const responder = (preguntaId: number, r: RespuestaLocal) =>
    setRespuestas((antes) => ({ ...antes, [preguntaId]: r }));

  /*
    Al cambiar de página se cierra lo que estuviera abierto. Sin esto, volver a
    una página deja desplegada la pregunta que se estaba respondiendo hace tres
    pantallas, que no es donde quedó la atención.
  */
  useEffect(() => {
    setAbierta(null);
    setAPantalla(null);
  }, [pagina]);

  const abrir = (p: Pregunta) => {
    if (abreEnPantallaCompleta(p)) setAPantalla(p.id);
    else setAbierta((antes) => (antes === p.id ? null : p.id));
  };

  const actual = pagina >= 0 ? paginas[pagina] : undefined;
  /*
    Desde la 036 el color puede ser un hex libre, así que se resuelve con
    `acentoDeColor` en vez de buscarlo en la paleta: un `ACENTOS['#1c4270']` no
    existe y caía al acento base, o sea que el color elegido se ignoraba.
  */
  const acento: Acento = acentoDeColor(actual?.seccion?.color);

  /*
    ⚠️ **El progreso cuenta lo RESPONDIDO, no lo visto.**

    Antes sumaba las preguntas de las páginas por las que se había pasado, así
    que **avanzar sin contestar nada llevaba la barra al 100 %**. Con la lista
    plegada eso pasa de inexacto a mentiroso: la pantalla diría «100 %» y debajo
    habría diez filas que dicen «sin responder».
  */
  const respondidas = preguntas.filter(
    (p) => !esSeccion(p.tipo) && estaRespondida(p, respuestas[p.id]),
  ).length;
  const progreso = total === 0 ? 0 : Math.round((respondidas / total) * 100);

  return (
    <Telefono>
      {/*
        La capa va **encima** de la página ya dibujada, no en su lugar: cuando se
        desvanece, lo que aparece debajo es el banner ya montado en su sitio. Esa
        es la contracción — no hay que animar una posición contra otra, que es
        donde este tipo de efecto se rompe en cuanto el texto cambia de largo.
      */}
      {entrada && actual?.seccion && (
        <button
          type="button"
          onClick={saltarEntrada}
          aria-label="Saltar la presentación de la sección"
          className={`absolute inset-0 z-20 flex flex-col justify-end text-left transition-all duration-500 ease-out
            ${entrada.saliendo ? 'pointer-events-none scale-[0.97] opacity-0' : 'scale-100 opacity-100'}`}
          style={{ background: acento.suave }}
        >
          {actual.seccion.tieneImagen && (
            <ImagenCuestionario
              ruta={`/api/cuestionarios/preguntas/${actual.seccion.id}/imagen`}
              hay
              alt=""
              className="absolute inset-0 h-full w-full"
            />
          )}
          {/*
            El degradado hace legible el texto encima de CUALQUIER imagen. Sin
            él, una sección con foto clara se queda sin título — y no se
            descubriría hasta que un cliente subiera una foto clara.
          */}
          {actual.seccion.tieneImagen && (
            <div className="absolute inset-0 bg-gradient-to-t from-marino-900/85 via-marino-900/35 to-transparent" />
          )}

          <div className="relative p-7 pb-16">
            <p
              className="text-xs font-bold uppercase tracking-widest"
              style={{ color: actual.seccion.tieneImagen ? '#fff' : acento.fuerte }}
            >
              Sección {paginas.filter((pg, i) => i <= pagina && pg.seccion).length}
            </p>
            <h3
              className={`mt-2 text-3xl font-bold leading-tight ${
                actual.seccion.tieneImagen ? 'text-white' : 'text-marino-900'
              }`}
            >
              {actual.seccion.emoji && (
                <span className="mr-2" aria-hidden="true">
                  {actual.seccion.emoji}
                </span>
              )}
              {actual.seccion.texto || 'Sección sin título'}
            </h3>
            {actual.seccion.ayuda && (
              <p
                className={`mt-3 text-base leading-relaxed ${
                  actual.seccion.tieneImagen ? 'text-white/85' : 'text-marino-600'
                }`}
              >
                {actual.seccion.ayuda}
              </p>
            )}
            <p
              className={`mt-6 text-xs font-semibold ${
                actual.seccion.tieneImagen ? 'text-white/70' : 'text-marino-400'
              }`}
            >
              Toca para continuar
            </p>
          </div>
        </button>
      )}

      {/*
        ── Pantalla completa para lo que no cabe en una fila (CC-12) ──
        Una matriz de seis afirmaciones, una barra que se arrastra, un grabador:
        apretados en una fila de lista se toca lo que no se quiso tocar. Se abren
        encima de todo, con su propio «Listo».

        Va antes de la portada en el JSX para que quede POR ENCIMA: si fuera
        después, el `z-20` competiría con la capa de entrada de sección.
      */}
      {aPantalla !== null &&
        (() => {
          const fila = actual?.preguntas.find((x) => x.pregunta.id === aPantalla);
          if (!fila) return null;
          const escala = escalas.find((e) => e.id === fila.pregunta.escalaId);
          return (
            <div className="absolute inset-0 z-20 flex flex-col bg-marino-50">
              <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-10">
                <PreguntaRespondible
                  pregunta={fila.pregunta}
                  numero={fila.numero}
                  escala={escala}
                  acento={acento}
                  respuesta={respuestas[fila.pregunta.id]}
                  alResponder={(r) => responder(fila.pregunta.id, r)}
                />
              </div>
              <div className="shrink-0 border-t border-marino-100 bg-white px-4 py-3">
                <button
                  type="button"
                  onClick={() => setAPantalla(null)}
                  className="w-full rounded-2xl py-3 text-sm font-bold transition
                    hover:brightness-110
                    focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                  style={{ background: acento.fuerte, color: acento.textoSobreFuerte }}
                >
                  Listo
                </button>
              </div>
            </div>
          );
        })()}

      {pagina === -1 ? (
        <Portada
          cuestionarioId={cuestionarioId}
          tienePortada={tienePortada}
          nombre={nombre}
          descripcion={descripcion}
          total={total}
          paginas={paginas.length}
          vacio={preguntas.length === 0}
          alComenzar={() => setPagina(0)}
        />
      ) : (
        <div className="flex h-full flex-col">
          {/* Barra de progreso: la meta corta que hace terminable un formulario largo. */}
          <div className="shrink-0 px-5 pt-4">
            <div className="flex items-center justify-between text-[11px] font-semibold text-marino-400">
              <span>
                {pagina + 1} de {paginas.length}
              </span>
              <span>{progreso}%</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-marino-100">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${progreso}%`, background: acento.fuerte }}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3">
            {actual?.seccion && (
              <header
                className="mb-3 overflow-hidden rounded-2xl"
                style={{ background: acento.suave }}
              >
                {/*
                  La imagen va DENTRO de la cabecera de color, arriba del título:
                  así el bloque sigue siendo una sola pieza y el acento de la
                  sección se lee igual. `overflow-hidden` en el padre es lo que
                  hace que la imagen respete las esquinas redondeadas.
                */}
                {actual.seccion.tieneImagen && (
                  <ImagenCuestionario
                    ruta={`/api/cuestionarios/preguntas/${actual.seccion.id}/imagen`}
                    hay
                    alt=""
                    className="h-28 w-full"
                  />
                )}
                <div className="p-4">
                <p
                  className="text-[11px] font-bold uppercase tracking-widest"
                  style={{ color: acento.fuerte }}
                >
                  Sección {paginas.filter((pg, i) => i <= pagina && pg.seccion).length}
                </p>
                <h3 className="mt-1 text-lg font-bold leading-snug text-marino-900">
                  {actual.seccion.emoji && (
                    <span className="mr-1.5" aria-hidden="true">
                      {actual.seccion.emoji}
                    </span>
                  )}
                  {actual.seccion.texto}
                </h3>
                {actual.seccion.ayuda && (
                  <p className="mt-1 text-sm text-marino-500">{actual.seccion.ayuda}</p>
                )}
                </div>
              </header>
            )}

            {actual && actual.preguntas.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-marino-200 bg-white px-4 py-8 text-center text-sm text-marino-400">
                Esta sección no tiene preguntas todavía.
              </p>
            ) : (
              <div className="space-y-2.5">
                {actual?.preguntas.map(({ pregunta, numero }) => {
                  const escala = escalas.find((e) => e.id === pregunta.escalaId);
                  /*
                    ⚠️ La que se está EDITANDO en el constructor se despliega
                    sola. Si se quedara plegada, tocar el texto de una pregunta
                    en el editor no mostraría nada del otro lado — y la previa en
                    vivo existe justo para eso. La navegación del trabajador es
                    una cosa; mirar lo que se está editando, otra.
                  */
                  const desplegada = abierta === pregunta.id || idResaltado === pregunta.id;

                  if (!desplegada) {
                    return (
                      <FilaPlegada
                        key={pregunta.id}
                        pregunta={pregunta}
                        numero={numero}
                        respuesta={respuestas[pregunta.id]}
                        escala={escala}
                        acento={acento}
                        alAbrir={() => abrir(pregunta)}
                      />
                    );
                  }

                  return (
                    <PreguntaRespondible
                      key={pregunta.id}
                      pregunta={pregunta}
                      numero={numero}
                      escala={escala}
                      acento={acento}
                      resaltada={pregunta.id === idResaltado}
                      respuesta={respuestas[pregunta.id]}
                      alResponder={(r) => responder(pregunta.id, r)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <nav className="flex shrink-0 items-center justify-between gap-3 border-t border-marino-100 bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => setPagina(pagina - 1)}
              className="rounded-xl px-3.5 py-2 text-sm font-semibold text-marino-500 transition
                hover:bg-marino-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
            >
              Atrás
            </button>
            {pagina < paginas.length - 1 ? (
              <button
                type="button"
                onClick={() => setPagina(pagina + 1)}
                className="rounded-xl px-5 py-2 text-sm font-bold transition hover:brightness-110
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                style={{ background: acento.fuerte, color: acento.textoSobreFuerte }}
              >
                Siguiente
              </button>
            ) : (
              <span
                inert
                className="rounded-xl px-5 py-2 text-sm font-bold opacity-90"
                style={{ background: acento.fuerte, color: acento.textoSobreFuerte }}
              >
                Enviar
              </span>
            )}
          </nav>
        </div>
      )}
    </Telefono>
  );
}

function Portada({
  cuestionarioId,
  tienePortada,
  nombre,
  descripcion,
  total,
  paginas,
  vacio,
  alComenzar,
}: {
  cuestionarioId: number;
  tienePortada: boolean;
  nombre: string;
  descripcion: string | null;
  total: number;
  paginas: number;
  vacio: boolean;
  alComenzar: () => void;
}) {
  return (
    /*
      ── El alto se reparte, no se suma ──
      Con `justify-between` y una portada de alto fijo, los tres bloques SUMABAN
      más que la pantalla y «Comenzar» se salía por abajo del marco — comprobado:
      el botón caía 137 px más abajo que el borde del teléfono. Y no se veía como
      un error, se veía como un teléfono sin botón.

      Ahora: la portada y el botón no se encogen, y **el texto del medio se lleva
      lo que sobre** y hace scroll si no le alcanza. Con o sin portada, en una
      ventana alta o en una baja, el botón queda siempre pegado abajo y dentro.
    */
    <div className="flex h-full flex-col">
      {/*
        La portada, si la hay: una banda a todo el ancho arriba del todo, pegada
        al borde de la pantalla. Por eso el relleno se mudó de este contenedor a
        los bloques de adentro — una carátula con margen alrededor se ve como una
        foto pegada, no como la cabecera de la pantalla.
      */}
      {tienePortada && (
        /*
          `h-[30%]` y no `h-44`: en un teléfono encogido —el visor se adapta al
          alto de la ventana— 176 px fijos se comen media pantalla. Un tercio se
          ve igual de bien en las dos. El `min-h` evita que en una ventana muy
          baja quede una franja demasiado angosta para reconocer la imagen.
        */
        <div className="relative h-[30%] min-h-[5rem] shrink-0 overflow-hidden">
          <ImagenCuestionario
            ruta={`/api/cuestionarios/${cuestionarioId}/portada`}
            hay={tienePortada}
            alt=""
            className="h-full w-full"
          />
          {/*
            Un degradado hacia el fondo de la pantalla. Sin él, el corte entre la
            foto y lo de abajo es una línea dura que delata el recorte; con él, la
            imagen entra en la pantalla en vez de estar encima.
          */}
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-marino-50 to-transparent" />
        </div>
      )}

      <div className={`min-h-0 flex-1 overflow-y-auto px-6 ${tienePortada ? 'pt-2' : 'pt-14'}`}>
        {/*
          El isotipo es un círculo marino: acá el fondo es claro, va directo.
          Con portada se cae: dos marcas apiladas compiten, y la que manda en la
          pantalla del trabajador es la del cuestionario.
        */}
        {!tienePortada && <img src="/copathy-isotipo.png" alt="" className="h-12 w-12" />}
        <h3 className="mt-5 text-2xl font-bold leading-tight text-marino-900">{nombre}</h3>
        {descripcion && <p className="mt-2 text-sm leading-relaxed text-marino-500">{descripcion}</p>}
        {!vacio && (
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-marino-400">
            {total} {total === 1 ? 'pregunta' : 'preguntas'}
            {paginas > 1 && ` · ${paginas} partes`}
          </p>
        )}
      </div>

      <div className="shrink-0 p-6 pt-3">
        {vacio ? (
          <p className="rounded-2xl border border-dashed border-marino-200 bg-white px-4 py-6 text-center text-sm text-marino-400">
            Lo que agregues en el constructor va apareciendo acá.
          </p>
        ) : (
          <button
            type="button"
            onClick={alComenzar}
            className="w-full rounded-2xl bg-naranja-500 py-3.5 font-bold text-marino-900 transition
              hover:bg-naranja-400 focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
          >
            Comenzar
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * El marco. Proporción de un teléfono real (~19.5:9), borde grueso y la isla de
 * la cámara. Lo justo para que se lea «teléfono» sin dibujar un iPhone con
 * marca registrada.
 */
/**
 * ── Medidas de un teléfono de verdad, y se escala para caber ──
 *
 * La pantalla mide **390 × 844**, que es el viewport lógico de un iPhone 14/15 y
 * anda a un pelo del Android corriente (360 × 800). No es decoración: a ese
 * tamaño el texto se corta donde se va a cortar de verdad, las caritas caben las
 * que van a caber, y un enunciado largo se ve largo acá también.
 *
 * ⚠️ **Y por eso se escala en vez de encogerse.** El intento anterior dejaba que
 * el marco se achicara con la ventana, y eso mentía: en una ventana baja el
 * teléfono medía 432 px de alto y el contenido se apretaba dentro, así que la
 * previa mostraba unos saltos de línea que en el teléfono real no ocurren.
 * Ahora el contenido SIEMPRE se dibuja a 390 × 844 y lo que cambia es el
 * `transform: scale`: es la misma pantalla vista de más lejos, no otra pantalla.
 */
const ANCHO_PANTALLA = 390;
const ALTO_PANTALLA = 844;
const BORDE = 11;

function Telefono({ children }: { children: React.ReactNode }) {
  const hueco = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(1);

  useEffect(() => {
    const el = hueco.current;
    if (!el) return;

    const medir = () => {
      const { height, width } = el.getBoundingClientRect();
      if (height === 0) return;
      /*
        Nunca por encima de 1: agrandar un teléfono más allá de su tamaño real
        volvería a mentir, ahora en la otra dirección.

        ⚠️ El elemento que se mide NO puede depender de la escala, o el
        ResizeObserver se dispararía a sí mismo. Por eso el hueco es el que manda
        y el marco escalado va dentro, sin afectar su tamaño (`position:
        absolute` lo saca del flujo).
      */
      setEscala(
        Math.min(1, height / (ALTO_PANTALLA + BORDE * 2), width / (ANCHO_PANTALLA + BORDE * 2)),
      );
    };

    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={hueco} className="relative min-h-0 w-full flex-1">
      <div
        className="absolute left-1/2 top-0 rounded-[3rem] bg-marino-900 shadow-xl"
        style={{
          width: ANCHO_PANTALLA + BORDE * 2,
          height: ALTO_PANTALLA + BORDE * 2,
          padding: BORDE,
          transform: `translateX(-50%) scale(${escala})`,
          transformOrigin: 'top center',
        }}
      >
        <div
          className="relative overflow-hidden rounded-[2.4rem] bg-marino-50"
          style={{ width: ANCHO_PANTALLA, height: ALTO_PANTALLA }}
        >
          {/* La isla de la cámara. Lo justo para que se lea «teléfono». */}
          {/* z-30: por encima incluso de la entrada de seccion a pantalla
              completa. La camara de un telefono no desaparece cuando una
              aplicacion se pone a pantalla completa. */}
          <div className="pointer-events-none absolute left-1/2 top-2.5 z-30 h-6 w-24 -translate-x-1/2 rounded-full bg-marino-900" />
          <div className="h-full pt-9">{children}</div>
        </div>
      </div>
    </div>
  );
}
